import React, { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { ethers } from "ethers";
import { VaultCard } from "./components/VaultCard";
import { useVaults } from "./hooks/useVaults";
import type { VaultInfo } from "./hooks/useVaults";
import { BASE_CHAIN_ID, COLORS } from "./constants";
import { LiquidityWater } from "./components/LiquidityWater";

declare global {
  interface Window {
    ethereum?: ethers.Eip1193Provider & {
      request: (args: { method: string; params?: unknown[] }) => Promise<unknown>;
      on?: (event: string, handler: (...args: unknown[]) => void) => void;
      removeListener?: (event: string, handler: (...args: unknown[]) => void) => void;
    };
  }
}

// ── How It Works ──────────────────────────────────────────────────────────────
const HowItWorks: React.FC = () => (
  <div style={hw.section}>
    <h3 style={hw.title}>How it works</h3>
    <div style={hw.steps}>
      <Step
        num="1"
        heading="Get LP tokens on Aerodrome"
        body="Add liquidity to any supported pool on Aerodrome Finance. You'll receive LP tokens representing your share of that pool."
        link={{ label: "Open Aerodrome →", href: "https://aerodrome.finance/liquidity" }}
      />
      <div style={hw.arrow}>→</div>
      <Step
        num="2"
        heading="Deposit LP tokens here"
        body="Deposit your LP tokens into the matching vault below. We stake them in Aerodrome's gauge on your behalf."
      />
      <div style={hw.arrow}>→</div>
      <Step
        num="3"
        heading="Rewards auto-compound"
        body="Our keeper harvests your AERO rewards every hour, swaps them back to LP, and restakes — growing your position automatically."
      />
    </div>
  </div>
);

const Step: React.FC<{
  num: string; heading: string; body: string;
  link?: { label: string; href: string };
}> = ({ num, heading, body, link }) => (
  <div style={hw.step}>
    <div style={hw.stepNum}>{num}</div>
    <div style={hw.stepHeading}>{heading}</div>
    <div style={hw.stepBody}>{body}</div>
    {link && (
      <a href={link.href} target="_blank" rel="noreferrer" style={hw.stepLink}>{link.label}</a>
    )}
  </div>
);

const hw: Record<string, React.CSSProperties> = {
  section: {
    background: COLORS.bgCard, border: `1px solid ${COLORS.border}`,
    borderRadius: 16, padding: "20px 24px", marginBottom: 20,
  },
  title: {
    margin: "0 0 16px", fontSize: 14, fontWeight: 700, color: COLORS.textSub,
    textTransform: "uppercase", letterSpacing: "0.08em",
  },
  steps: { display: "flex", alignItems: "flex-start", gap: 4, flexWrap: "wrap" as const },
  arrow: { color: COLORS.border, fontSize: 18, padding: "20px 0 0", flexShrink: 0 },
  step:  { flex: 1, minWidth: 120 },
  stepNum: {
    width: 24, height: 24, borderRadius: "50%", background: COLORS.primary,
    color: "#fff", fontSize: 12, fontWeight: 800,
    display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 8,
  },
  stepHeading: { fontSize: 13, fontWeight: 700, color: COLORS.textPrimary, marginBottom: 4 },
  stepBody:    { fontSize: 12, color: COLORS.textMuted, lineHeight: 1.6 },
  stepLink:    { display: "inline-block", marginTop: 6, fontSize: 12, color: COLORS.accent, textDecoration: "none", fontWeight: 600 },
};

// ── Sort types ─────────────────────────────────────────────────────────────────
type SortKey = "apr" | "tvl" | "pending" | "position";

function sortVaults(vaults: VaultInfo[], key: SortKey): VaultInfo[] {
  return [...vaults].sort((a, b) => {
    switch (key) {
      case "apr":
        return (b.aprCompounded ?? b.apr ?? -1) - (a.aprCompounded ?? a.apr ?? -1);
      case "tvl":
        return parseFloat(b.totalLpManaged) - parseFloat(a.totalLpManaged);
      case "pending":
        return parseFloat(b.pendingRewards) - parseFloat(a.pendingRewards);
      case "position":
        return parseFloat(b.userLpValue) - parseFloat(a.userLpValue);
    }
  });
}

// ── Compact vault row ──────────────────────────────────────────────────────────
const VaultRow: React.FC<{
  vault: VaultInfo;
  expanded: boolean;
  onToggle: () => void;
  signer: ethers.Signer | null;
  onRefresh: () => void;
}> = ({ vault, expanded, onToggle, signer, onRefresh }) => {
  const autoApr = vault.aprCompounded ?? vault.apr;
  const baseApr = vault.apr;
  const boost = (autoApr !== null && baseApr !== null && autoApr > baseApr)
    ? +(autoApr - baseApr).toFixed(1)
    : null;
  const hasPosition = parseFloat(vault.userShares) > 0;

  return (
    <div style={row.wrapper}>
      {/* Compact row — always visible */}
      <button style={row.header} onClick={onToggle}>
        <div style={row.left}>
          <span style={row.name}>{vault.name}</span>
          <span style={{
            ...row.badge,
            background: vault.isStable ? "#0d2d1f" : "#1a1428",
            color:      vault.isStable ? "#4ade80" : "#a78bfa",
            border:     `1px solid ${vault.isStable ? "#166534" : "#6d28d9"}`,
          }}>
            {vault.isStable ? "Stable" : "Volatile"}
          </span>
          {hasPosition && <span style={row.positionDot} title="You have a position">●</span>}
        </div>
        <div style={row.right}>
          {autoApr !== null ? (
            <span style={{ display: "flex", alignItems: "baseline", gap: 5, justifyContent: "flex-end" }}>
              <span style={{
                ...row.apr,
                color: autoApr > 50 ? COLORS.green : autoApr > 20 ? COLORS.yellow : COLORS.accent,
              }}>
                {autoApr.toFixed(1)}%
              </span>
              {boost !== null && boost > 0 && (
                <span style={row.boostBadge}>+{boost.toFixed(1)}%</span>
              )}
            </span>
          ) : (
            <span style={{ ...row.apr, color: COLORS.textMuted }}>—</span>
          )}
          <span style={row.tvl}>{fmtTvl(vault.poolTvlUsd)}</span>
          <span style={row.chevron}>{expanded ? "▲" : "▼"}</span>
        </div>
      </button>

      {/* Expanded full card */}
      {expanded && (
        <div style={row.expanded}>
          <VaultCard vault={vault} signer={signer} onRefresh={onRefresh} />
        </div>
      )}
    </div>
  );
};

function fmtLp(val: string): string {
  const n = parseFloat(val);
  if (!n) return "0";
  if (n >= 1000) return n.toFixed(0);
  if (n >= 1)    return n.toFixed(2);
  return n.toFixed(6);
}

function fmtTvl(usd: number | null): string {
  if (usd === null || usd === 0) return "—";
  if (usd >= 1_000_000) return "$" + (usd / 1_000_000).toFixed(1) + "M";
  if (usd >= 1_000)     return "$" + (usd / 1_000).toFixed(0) + "K";
  return "$" + usd.toFixed(0);
}

const row: Record<string, React.CSSProperties> = {
  wrapper: {
    background: COLORS.bgCard,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 12,
    marginBottom: 8,
    overflow: "hidden",
  },
  header: {
    width: "100%", display: "flex", justifyContent: "space-between",
    alignItems: "center", padding: "14px 18px",
    background: "transparent", border: "none",
    cursor: "pointer", gap: 8,
  },
  left: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" as const },
  name: { fontSize: 15, fontWeight: 700, color: COLORS.textPrimary },
  badge: {
    fontSize: 10, fontWeight: 700, letterSpacing: "0.08em",
    textTransform: "uppercase" as const, padding: "2px 6px", borderRadius: 4,
  },
  positionDot: { fontSize: 8, color: COLORS.green },
  right: { display: "flex", alignItems: "center", gap: 16, flexShrink: 0 },
  apr:  { fontSize: 15, fontWeight: 800, minWidth: 60, textAlign: "right" as const },
  tvl:  { fontSize: 12, color: COLORS.textMuted, minWidth: 80, textAlign: "right" as const },
  chevron: { fontSize: 10, color: COLORS.textMuted, width: 12 },
  expanded: { borderTop: `1px solid ${COLORS.border}`, padding: "0" },
  boostBadge: {
    fontSize: 10, fontWeight: 700,
    color: "#34d399", background: "rgba(52,211,153,0.12)",
    border: "1px solid rgba(52,211,153,0.25)",
    borderRadius: 4, padding: "1px 5px",
    letterSpacing: "0.02em", whiteSpace: "nowrap" as const,
  },
};

// ── App ───────────────────────────────────────────────────────────────────────
export default function App() {
  const [provider, setProvider] = useState<ethers.BrowserProvider | null>(null);
  const [signer, setSigner]     = useState<ethers.Signer | null>(null);
  const [address, setAddress]   = useState<string | null>(null);
  const [connErr, setConnErr]   = useState<string | null>(null);
  const [sortKey, setSortKey]   = useState<SortKey>("apr");
  const [expandedVault, setExpandedVault] = useState<string | null>(null);

  const { vaults, loading, error, refresh } = useVaults(address, provider);

  const sortedVaults = useMemo(() => sortVaults(vaults, sortKey), [vaults, sortKey]);

  // ── Helpers ─────────────────────────────────────────────────────────────────
  const disconnect = useCallback(() => {
    setProvider(null); setSigner(null); setAddress(null); setConnErr(null);
  }, []);

  // Core connect logic — extracted so event handlers can call it too
  const doConnect = useCallback(async (silent = false) => {
    if (!silent) setConnErr(null);
    if (!window.ethereum) {
      setConnErr("No wallet detected. Install MetaMask or Rabby.");
      return;
    }
    try {
      // Always create a fresh provider to pick up the current network
      const prov    = new ethers.BrowserProvider(window.ethereum);
      const network = await prov.getNetwork();

      if (Number(network.chainId) !== BASE_CHAIN_ID) {
        setConnErr("Switching to Base network…");
        try {
          await window.ethereum.request({
            method: "wallet_switchEthereumChain",
            params: [{ chainId: `0x${BASE_CHAIN_ID.toString(16)}` }],
          });
        } catch {
          await window.ethereum.request({
            method: "wallet_addEthereumChain",
            params: [{
              chainId: `0x${BASE_CHAIN_ID.toString(16)}`,
              chainName: "Base",
              nativeCurrency: { name: "Ether", symbol: "ETH", decimals: 18 },
              rpcUrls: ["https://mainnet.base.org"],
              blockExplorerUrls: ["https://basescan.org"],
            }],
          });
        }
        // wallet_switchEthereumChain resolves before the provider re-emits
        // so re-create the provider after the switch
        const freshProv = new ethers.BrowserProvider(window.ethereum);
        const sign = await freshProv.getSigner();
        const addr = await sign.getAddress();
        setConnErr(null);
        setProvider(freshProv); setSigner(sign); setAddress(addr);
        return;
      }

      const sign = await prov.getSigner();
      const addr = await sign.getAddress();
      setConnErr(null);
      setProvider(prov); setSigner(sign); setAddress(addr);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      // User rejected the switch — don't show a scary error
      if (msg.includes("User rejected") || msg.includes("user rejected")) {
        setConnErr("Please switch to the Base network in your wallet.");
      } else if (!silent) {
        setConnErr("Connection failed. Please try again.");
      }
    }
  }, []);

  const connect = useCallback(() => doConnect(false), [doConnect]);

  // ── Listen for wallet network / account changes ──────────────────────────
  useEffect(() => {
    const eth = window.ethereum;
    if (!eth) return;

    const onChainChanged = () => {
      // Network changed — silently reconnect (will switch back to Base if needed)
      // Small delay so MetaMask has finished the switch
      setTimeout(() => doConnect(true), 300);
    };

    const onAccountsChanged = (accounts: unknown) => {
      const accs = accounts as string[];
      if (!accs || accs.length === 0) {
        disconnect();
      } else {
        doConnect(true);
      }
    };

    eth.on?.("chainChanged",    onChainChanged);
    eth.on?.("accountsChanged", onAccountsChanged);
    return () => {
      eth.removeListener?.("chainChanged",    onChainChanged);
      eth.removeListener?.("accountsChanged", onAccountsChanged);
    };
  }, [doConnect, disconnect]);

  const totalTvlLp      = vaults.reduce((s, v) => s + parseFloat(v.totalLpManaged || "0"), 0);
  const totalPending    = vaults.reduce((s, v) => s + parseFloat(v.pendingRewards || "0"), 0);
  const totalCompounded = vaults.reduce((s, v) => s + parseFloat(v.totalRewardsCompounded || "0"), 0);
  const totalFees       = vaults.reduce((s, v) => s + parseFloat(v.totalFeesCollected || "0"), 0);
  const userTotalLp     = vaults.reduce((s, v) => s + parseFloat(v.userLpValue || "0"), 0);
  const hasAnyPosition  = vaults.some(v => parseFloat(v.userShares) > 0);

  // ── LiquidityWater: capture baseline LP on first non-zero load ───────────
  const initialLpRef = useRef<number | null>(null);
  useEffect(() => {
    if (initialLpRef.current === null && userTotalLp > 0) {
      initialLpRef.current = userTotalLp;
    }
  }, [userTotalLp]);

  const aprVaults = vaults.filter(v => v.aprCompounded !== null);
  const avgApr    = aprVaults.length
    ? aprVaults.reduce((s, v) => s + (v.aprCompounded ?? 0), 0) / aprVaults.length
    : 0;

  const SORT_OPTIONS: { key: SortKey; label: string }[] = [
    { key: "apr",      label: "APR" },
    { key: "tvl",      label: "TVL" },
    { key: "pending",  label: "Rewards" },
    { key: "position", label: "My Position" },
  ];

  return (
    <div style={s.root}>
      <LiquidityWater
        userLP={userTotalLp}
        initialLP={initialLpRef.current ?? 0}
        pendingRewards={totalPending}
        apr={avgApr}
      />
      <div style={s.container}>

        {/* Header */}
        <header style={s.header}>
          <div>
            <div style={s.logoRow}>
              <span style={s.logoIcon}>⚡</span>
              <span style={s.logoText}>AeroCompounder</span>
              <span style={s.networkBadge}>Base</span>
            </div>
            <p style={s.tagline}>Auto-compound your Aerodrome LP rewards</p>
          </div>
          {address ? (
            <div style={s.walletBox}>
              <div style={s.walletAddr}>{address.slice(0, 6)}…{address.slice(-4)}</div>
              <button style={s.disconnectBtn} onClick={disconnect}>✕</button>
            </div>
          ) : (
            <button style={s.connectBtn} onClick={connect}>Connect Wallet</button>
          )}
        </header>

        {/* Error — filter out ethers internal network-change noise */}
        {(() => {
          const vaultErr = error && !error.includes("NETWORK_ERROR") && !error.includes("network changed") ? error : null;
          const msg = connErr || vaultErr;
          if (!msg) return null;
          const isInfo = msg.includes("Switching to Base");
          return (
            <div style={{ ...s.errBox, ...(isInfo ? s.infoBox : {}) }}>{msg}</div>
          );
        })()}

        {/* Protocol Stats */}
        {provider && vaults.length > 0 && (
          <div style={s.protocolStats}>
            <ProtocolStat label="Vaults"           value={`${vaults.length}`} />
            <ProtocolStat label="Total LP Managed" value={fmtNum(totalTvlLp, 4) + " LP"} />
            <ProtocolStat label="Pending Rewards"  value={fmtNum(totalPending, 4) + " AERO"} accent />
            <ProtocolStat label="Total Compounded" value={fmtNum(totalCompounded, 4) + " AERO"} />
            <ProtocolStat label="Fees Collected"   value={fmtNum(totalFees, 4) + " AERO"} />
            {hasAnyPosition && (
              <ProtocolStat label="Your Total LP"  value={fmtNum(userTotalLp, 6) + " LP"} accent />
            )}
          </div>
        )}

        {/* No wallet */}
        {!address && (
          <>
            <div style={s.connectPrompt}>
              <div style={s.promptIcon}>🌊</div>
              <h2 style={s.promptTitle}>Auto-compound your Aerodrome yields</h2>
              <p style={s.promptText}>
                Deposit your Aerodrome LP tokens and let AeroCompounder harvest,
                swap, and reinvest your AERO rewards automatically.
              </p>
              <ul style={s.promptList}>
                <li>2% performance fee on harvested rewards</li>
                <li>0.1% withdrawal fee</li>
                <li>Non-custodial — withdraw anytime</li>
                <li>Multiple pools supported</li>
              </ul>
              <button style={s.connectBtnLarge} onClick={connect}>
                Connect Wallet to Start
              </button>
            </div>
            <HowItWorks />
          </>
        )}

        {/* Vaults */}
        {provider && (
          <>
            {loading && vaults.length === 0 ? (
              <div style={s.loading}>Loading vaults...</div>
            ) : vaults.length === 0 ? (
              <div style={s.empty}>No vaults deployed yet.</div>
            ) : (
              <>
                <HowItWorks />

                {/* Sort + Refresh bar */}
                <div style={s.vaultToolbar}>
                  <div style={s.sortGroup}>
                    <span style={s.sortLabel}>Sort:</span>
                    {SORT_OPTIONS.map(opt => (
                      <button
                        key={opt.key}
                        style={{ ...s.sortBtn, ...(sortKey === opt.key ? s.sortBtnActive : {}) }}
                        onClick={() => setSortKey(opt.key)}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                  <button style={s.refreshBtn} onClick={refresh}>↻</button>
                </div>

                {/* Column headers */}
                <div style={s.colHeaders}>
                  <span style={{ flex: 1 }}>Pool</span>
                  <span style={{ minWidth: 70, textAlign: "right" as const }}>Auto APR</span>
                  <span style={{ minWidth: 90, textAlign: "right" as const }}>TVL</span>
                  <span style={{ width: 12 }} />
                </div>

                {sortedVaults.map((vault) => (
                  <VaultRow
                    key={vault.address}
                    vault={vault}
                    expanded={expandedVault === vault.address}
                    onToggle={() => setExpandedVault(
                      expandedVault === vault.address ? null : vault.address
                    )}
                    signer={signer}
                    onRefresh={refresh}
                  />
                ))}
              </>
            )}
          </>
        )}

        {/* Footer */}
        <footer style={s.footer}>
          <div style={s.footerLinks}>
            <a href="https://x.com/aerocompounder" style={s.footerXLink} target="_blank" rel="noreferrer">
              <svg style={{ display: "block" }} width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.746l7.73-8.835L1.254 2.25H8.08l4.259 5.631L18.244 2.25zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77z"/>
              </svg>
              @aerocompounder
            </a>
            <span style={{ color: COLORS.border }}>·</span>
            <a href="https://aerocompounder.com" style={s.footerLink}>aerocompounder.com</a>
            <span style={{ color: COLORS.border }}>·</span>
            <a href="https://aerodrome.finance" style={s.footerLink} target="_blank" rel="noreferrer">Aerodrome</a>
            <span style={{ color: COLORS.border }}>·</span>
            <a href="https://basescan.org" style={s.footerLink} target="_blank" rel="noreferrer">BaseScan</a>
            <span style={{ color: COLORS.border }}>·</span>
            <a href="https://github.com/0xMalva/base-aero-autocompounder" style={s.footerLink} target="_blank" rel="noreferrer">
              <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 0C5.374 0 0 5.373 0 12c0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23A11.509 11.509 0 0 1 12 5.803c1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576C20.566 21.797 24 17.3 24 12c0-6.627-5.373-12-12-12z"/>
                </svg>
                GitHub
              </span>
            </a>
          </div>
          <p style={s.footerText}>Non-custodial. Audits pending. Use at your own risk.</p>
        </footer>

      </div>
    </div>
  );
}

function fmtNum(n: number, decimals = 4): string {
  if (n === 0) return "0";
  if (n > 0 && n < Math.pow(10, -decimals)) {
    const magnitude = Math.floor(Math.log10(n));
    return n.toFixed(Math.min(Math.abs(magnitude) + 2, 12));
  }
  return n.toFixed(decimals);
}

const ProtocolStat: React.FC<{ label: string; value: string; accent?: boolean }> = ({ label, value, accent }) => (
  <div style={{ textAlign: "center" as const }}>
    <div style={{ fontSize: 11, color: COLORS.textMuted, textTransform: "uppercase" as const, letterSpacing: "0.06em", marginBottom: 4 }}>{label}</div>
    <div style={{ fontSize: 20, fontWeight: 800, color: accent ? COLORS.green : COLORS.textPrimary }}>{value}</div>
  </div>
);

const s: Record<string, React.CSSProperties> = {
  root: { minHeight: "100vh", position: "relative", color: COLORS.textPrimary, fontFamily: "'Inter', 'Segoe UI', sans-serif", padding: "24px 16px 48px" },
  container: { maxWidth: 560, margin: "0 auto", position: "relative", zIndex: 1 },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 28 },
  logoRow:  { display: "flex", alignItems: "center", gap: 8, marginBottom: 4 },
  logoIcon: { fontSize: 22 },
  logoText: { fontSize: 22, fontWeight: 900, color: COLORS.textPrimary, letterSpacing: "-0.5px" },
  networkBadge: { fontSize: 11, background: "#1e2d5a", color: "#60a5fa", border: "1px solid #1e40af44", borderRadius: 4, padding: "2px 8px", fontWeight: 700 },
  tagline: { margin: 0, fontSize: 13, color: COLORS.textMuted },
  walletBox: { display: "flex", alignItems: "center", gap: 6 },
  walletAddr: { fontSize: 13, color: COLORS.textSub, background: COLORS.bgCard, border: `1px solid ${COLORS.border}`, padding: "6px 12px", borderRadius: 8 },
  disconnectBtn: { padding: "6px 8px", borderRadius: 6, border: "none", background: COLORS.bgCard, color: COLORS.textMuted, cursor: "pointer", fontSize: 12 },
  connectBtn: { padding: "10px 18px", borderRadius: 8, border: "none", background: COLORS.primary, color: "#fff", fontWeight: 700, fontSize: 14, cursor: "pointer" },
  errBox: { background: "#1f1015", border: "1px solid #7f1d1d", color: COLORS.red, borderRadius: 8, padding: "10px 14px", fontSize: 13, marginBottom: 16 },
  infoBox: { background: "#0f1a2e", border: "1px solid #1e3a5f", color: "#60a5fa" },
  protocolStats: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, background: COLORS.bgCard, border: `1px solid ${COLORS.border}`, borderRadius: 12, padding: 20, marginBottom: 20 },
  connectPrompt: { background: COLORS.bgCard, border: `1px solid ${COLORS.border}`, borderRadius: 16, padding: 32, textAlign: "center" as const, marginBottom: 16 },
  promptIcon:  { fontSize: 40, marginBottom: 12 },
  promptTitle: { margin: "0 0 12px", fontSize: 20, fontWeight: 800, color: COLORS.textPrimary },
  promptText:  { color: COLORS.textSub, fontSize: 14, lineHeight: 1.6, margin: "0 0 16px" },
  promptList:  { listStyle: "none", padding: 0, margin: "0 0 24px", color: COLORS.textMuted, fontSize: 13, lineHeight: 2 },
  connectBtnLarge: { padding: "14px 32px", borderRadius: 10, border: "none", background: COLORS.primary, color: "#fff", fontWeight: 700, fontSize: 16, cursor: "pointer" },
  vaultToolbar: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 },
  sortGroup:    { display: "flex", alignItems: "center", gap: 4 },
  sortLabel:    { fontSize: 11, color: COLORS.textMuted, marginRight: 4, textTransform: "uppercase" as const, letterSpacing: "0.06em" },
  sortBtn: {
    padding: "4px 10px", borderRadius: 6, border: `1px solid ${COLORS.border}`,
    background: "transparent", color: COLORS.textMuted, cursor: "pointer", fontSize: 12, fontWeight: 600,
  },
  sortBtnActive: { background: COLORS.primary, color: "#fff", border: `1px solid ${COLORS.primary}` },
  refreshBtn: { padding: "4px 10px", borderRadius: 6, border: `1px solid ${COLORS.border}`, background: "transparent", color: COLORS.textMuted, cursor: "pointer", fontSize: 12 },
  colHeaders: { display: "flex", alignItems: "center", padding: "0 18px 6px", fontSize: 11, color: COLORS.textMuted, textTransform: "uppercase" as const, letterSpacing: "0.06em", gap: 8 },
  loading: { color: COLORS.textMuted, textAlign: "center" as const, padding: 48 },
  empty:   { color: COLORS.textMuted, textAlign: "center" as const, padding: 48 },
  footer:  { marginTop: 32, textAlign: "center" as const },
  footerLinks: { display: "flex", justifyContent: "center", alignItems: "center", gap: 12, marginBottom: 8, flexWrap: "wrap" as const },
  footerLink:  { color: COLORS.textMuted, fontSize: 12, textDecoration: "none" },
  footerXLink: { display: "flex", alignItems: "center", gap: 5, color: COLORS.textSub, fontSize: 12, textDecoration: "none", fontWeight: 600 },
  footerText:  { color: "#374151", fontSize: 11, margin: 0 },
};